import { useEffect, useRef, useState } from 'react'
import type { Simulation, SimulationNodeDatum } from 'd3-force'
import type { ZoomBehavior } from 'd3-zoom'
import 'd3-transition'
import { Loader2 } from 'lucide-react'
import type { GraphEdge, GraphNode } from '@/types/api'
import { cn } from '@/lib/utils'
import { DOMAIN_OTHER } from './graphPalette'

/** d3-force mutates these in place each tick (x/y/vx/vy/fx/fy). */
interface SimNode extends GraphNode, SimulationNodeDatum {}

interface SimLink {
  source: SimNode
  target: SimNode
  edge: GraphEdge
}

/** Node radius from `rows_est` — log scale so a 10-row table and a
 * 10-million-row table don't collapse the rest of the graph to dust. */
export function radiusOf(n: GraphNode): number {
  return 9 + Math.min(22, Math.log10(Math.max(1, n.rows_est)) * 4.5)
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

const ZOOM_BTN =
  'text-text-2 hover:bg-wash hover:text-text grid size-8 place-items-center text-[16px] font-medium transition-colors duration-150'

export function ForceGraph({
  nodes,
  edges,
  domainColor,
  selectedId,
  onSelect,
  activeDomain,
  searchQuery,
  onReadyChange,
  showLayoutStatus = true,
}: {
  nodes: GraphNode[]
  edges: GraphEdge[]
  domainColor: (domain: string) => string
  selectedId: string | null
  onSelect: (id: string | null) => void
  /** Non-null → only this domain keeps its real color; every other node
   * fades (click-to-highlight, not a remove-filter). */
  activeDomain?: string | null
  /** Non-empty → nodes whose label doesn't match fade out. */
  searchQuery?: string
  /** Fires when the force simulation finishes its initial settle pass. */
  onReadyChange?: (ready: boolean) => void
  /** Inline “Laying out the graph…” badge — off when the page owns loading. */
  showLayoutStatus?: boolean
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const zoomGRef = useRef<SVGGElement>(null)
  const simRef = useRef<Simulation<SimNode, SimLink> | null>(null)
  const zoomRef = useRef<ZoomBehavior<SVGSVGElement, unknown> | null>(null)
  const nodeRefs = useRef<Map<string, SVGGElement>>(new Map())
  const linkRefs = useRef<Map<number, SVGLineElement>>(new Map())
  const simNodesRef = useRef<SimNode[]>([])
  const onSelectRef = useRef(onSelect)
  const selectedIdRef = useRef(selectedId)
  const lastPickAt = useRef(0)
  onSelectRef.current = onSelect
  selectedIdRef.current = selectedId

  const pickNode = (id: string | null) => {
    const now = performance.now()
    if (now - lastPickAt.current < 80) return
    lastPickAt.current = now
    onSelectRef.current(id)
  }
  const [ready, setReady] = useState(false)
  const [, setTick] = useState(0)
  const [simNodes, setSimNodes] = useState<SimNode[]>([])
  const [simLinks, setSimLinks] = useState<SimLink[]>([])

  const fitToView = () => {
    const svg = svgRef.current
    const zb = zoomRef.current
    const current = simNodesRef.current
    if (!svg || !zb || current.length === 0) return
    const xs = current.map((n) => n.x ?? 0)
    const ys = current.map((n) => n.y ?? 0)
    const pad = 60
    const minX = Math.min(...xs) - pad
    const maxX = Math.max(...xs) + pad
    const minY = Math.min(...ys) - pad
    const maxY = Math.max(...ys) + pad
    const { width, height } = svg.getBoundingClientRect()
    if (width < 8 || height < 8) return
    const scale = Math.min(
      3,
      Math.max(0.15, Math.min(width / (maxX - minX), height / (maxY - minY))),
    )
    const tx = width / 2 - ((minX + maxX) / 2) * scale
    const ty = height / 2 - ((minY + maxY) / 2) * scale
    const duration = prefersReducedMotion() ? 0 : 300
    void import('d3-selection').then(({ select }) => {
      void import('d3-zoom').then(({ zoomIdentity }) => {
        const sel = select(svg)
        const t = zoomIdentity.translate(tx, ty).scale(scale)
        if (duration === 0) {
          sel.call(zb.transform, t)
        } else {
          sel.transition().duration(duration).call(zb.transform, t)
        }
      })
    })
  }

  const zoomBy = (factor: number) => {
    const svg = svgRef.current
    const zb = zoomRef.current
    if (!svg || !zb) return
    const duration = prefersReducedMotion() ? 0 : 200
    void import('d3-selection').then(({ select }) => {
      const sel = select(svg)
      if (duration === 0) {
        sel.call(zb.scaleBy, factor)
      } else {
        sel.transition().duration(duration).call(zb.scaleBy, factor)
      }
    })
  }

  useEffect(() => {
    setReady(false)
    onReadyChange?.(false)
  }, [nodes, edges, onReadyChange])

  useEffect(() => {
    onReadyChange?.(ready)
  }, [ready, onReadyChange])

  useEffect(() => {
    let cancelled = false
    let sim: Simulation<SimNode, SimLink> | null = null
    let settleTimer: number | undefined

    void (async () => {
      const [
        {
          forceSimulation,
          forceLink,
          forceManyBody,
          forceCenter,
          forceCollide,
          forceX,
          forceY,
        },
        { zoom },
        { select },
        { drag },
      ] = await Promise.all([
        import('d3-force'),
        import('d3-zoom'),
        import('d3-selection'),
        import('d3-drag'),
        import('d3-transition'),
      ])
      if (cancelled || !containerRef.current || !svgRef.current) return

      const rect = containerRef.current.getBoundingClientRect()
      const width = Math.max(rect.width, 1)
      const height = Math.max(rect.height, 1)
      const byId = new Map<string, SimNode>()
      const nextNodes: SimNode[] = nodes.map((n) => {
        const prior = simRef.current?.nodes().find((p) => p.id === n.id) as
          SimNode | undefined
        const node: SimNode = { ...n, x: prior?.x, y: prior?.y }
        byId.set(n.id, node)
        return node
      })
      const nextLinks: SimLink[] = edges
        .filter((e) => byId.has(e.from) && byId.has(e.to))
        .map((e) => ({
          source: byId.get(e.from)!,
          target: byId.get(e.to)!,
          edge: e,
        }))

      simNodesRef.current = nextNodes
      setSimNodes(nextNodes)
      setSimLinks(nextLinks)

      sim = forceSimulation(nextNodes)
        .force(
          'link',
          forceLink<SimNode, SimLink>(nextLinks)
            .id((d) => d.id)
            .distance(130)
            .strength(0.25),
        )
        .force('charge', forceManyBody().strength(-420))
        .force('center', forceCenter(width / 2, height / 2))
        .force('x', forceX(width / 2).strength(0.03))
        .force('y', forceY(height / 2).strength(0.03))
        .force(
          'collide',
          forceCollide<SimNode>().radius((d) => radiusOf(d) + 34),
        )
        .on('tick', () => {
          for (const n of nextNodes) {
            const el = nodeRefs.current.get(n.id)
            if (el && n.x != null && n.y != null) {
              el.setAttribute('transform', `translate(${n.x},${n.y})`)
            }
          }
          nextLinks.forEach((l, i) => {
            const el = linkRefs.current.get(i)
            if (el && l.source.x != null && l.target.x != null) {
              el.setAttribute('x1', String(l.source.x))
              el.setAttribute('y1', String(l.source.y))
              el.setAttribute('x2', String(l.target.x))
              el.setAttribute('y2', String(l.target.y))
            }
          })
          setTick((t) => t + 1)
        })
      simRef.current = sim

      const svgSel = select(svgRef.current)
      const zoomBehavior = zoom<SVGSVGElement, unknown>()
        .scaleExtent([0.15, 3])
        .filter((event) => {
          if (event.type === 'wheel') return true
          const t = event.target as Element | null
          if (t?.closest('[data-kg-node]')) return false
          return !event.ctrlKey && !event.button
        })
        .on('zoom', (event) => {
          zoomGRef.current?.setAttribute(
            'transform',
            event.transform.toString(),
          )
        })
      svgSel.call(zoomBehavior)
      zoomRef.current = zoomBehavior

      const attachDrag = () => {
        if (cancelled) return
        for (const n of nextNodes) {
          const el = nodeRefs.current.get(n.id)
          if (!el) continue
          let moved = false
          select(el).call(
            drag<SVGGElement, unknown>()
              .on('start', (event) => {
                moved = false
                if (!event.active) sim?.alphaTarget(0.2).restart()
                n.fx = n.x
                n.fy = n.y
              })
              .on('drag', (event) => {
                if (Math.hypot(event.dx, event.dy) > 2) moved = true
                n.fx = event.x
                n.fy = event.y
              })
              .on('end', (event) => {
                if (!event.active) sim?.alphaTarget(0)
                n.fx = null
                n.fy = null
                if (!moved) {
                  const selected = selectedIdRef.current === n.id
                  pickNode(selected ? null : n.id)
                }
              }),
          )
        }
      }
      // Node <g> refs exist only after React commits simNodes.
      requestAnimationFrame(attachDrag)

      const settleMs = prefersReducedMotion() ? 0 : 600
      settleTimer = window.setTimeout(() => {
        if (!cancelled) setReady(true)
      }, settleMs)
    })()

    return () => {
      cancelled = true
      if (settleTimer != null) window.clearTimeout(settleTimer)
      sim?.stop()
      simRef.current = null
      zoomRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, edges])

  useEffect(() => {
    if (ready) fitToView()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready])

  const query = (searchQuery ?? '').trim().toLowerCase()

  const isMuted = (n: GraphNode) => {
    if (activeDomain && n.domain !== activeDomain) return true
    if (query && !n.label.toLowerCase().includes(query)) return true
    return false
  }

  return (
    <div className="relative h-full min-h-0 w-full overflow-hidden">
      <div ref={containerRef} className="h-full w-full">
        <svg
          ref={svgRef}
          role="img"
          aria-label="Business knowledge graph"
          className="h-full w-full touch-none"
        >
          <g ref={zoomGRef}>
            <g aria-hidden>
              {simLinks.map((l, i) => (
                <line
                  key={`${l.edge.from}-${l.edge.to}-${i}`}
                  ref={(el) => {
                    if (el) linkRefs.current.set(i, el)
                    else linkRefs.current.delete(i)
                  }}
                  className="stroke-border-2"
                  strokeWidth={1}
                  opacity={isMuted(l.source) && isMuted(l.target) ? 0.15 : 0.6}
                />
              ))}
            </g>
            <g>
              {simNodes.map((n) => {
                const r = radiusOf(n)
                const selected = n.id === selectedId
                const muted = isMuted(n)
                return (
                  <g
                    key={n.id}
                    ref={(el) => {
                      if (el) nodeRefs.current.set(n.id, el)
                      else nodeRefs.current.delete(n.id)
                    }}
                    className="cursor-pointer"
                    data-kg-node={n.id}
                    onClick={() => pickNode(selected ? null : n.id)}
                  >
                    <circle
                      r={r}
                      fill={muted ? DOMAIN_OTHER : domainColor(n.domain)}
                      stroke={
                        selected ? 'var(--color-text)' : 'var(--color-bg)'
                      }
                      strokeWidth={selected ? 2.5 : 1.5}
                      opacity={muted ? 0.35 : 0.9}
                    />
                    <text
                      y={r + 13}
                      textAnchor="middle"
                      className={cn(
                        'pointer-events-none fill-current text-[10px] font-medium',
                        muted
                          ? 'text-text-3'
                          : selected
                            ? 'text-text'
                            : 'text-text-2',
                      )}
                      opacity={muted ? 0.5 : 1}
                    >
                      {n.label}
                    </text>
                  </g>
                )
              })}
            </g>
          </g>
        </svg>
      </div>

      {showLayoutStatus && !ready && (
        <div
          role="status"
          className="border-border bg-card text-text-2 absolute inset-0 m-auto flex h-fit w-fit items-center gap-2 rounded-full border px-4 py-2 text-[13px] shadow-sm"
        >
          <Loader2 className="size-3.5 animate-spin" aria-hidden />
          Laying out the graph…
        </div>
      )}

      <div className="border-border bg-card absolute right-3 bottom-3 flex flex-col overflow-hidden rounded-[10px] border shadow-sm">
        <button
          type="button"
          aria-label="Zoom in"
          onClick={() => zoomBy(1.3)}
          className={cn(ZOOM_BTN, 'border-border border-b')}
        >
          +
        </button>
        <button
          type="button"
          aria-label="Zoom out"
          onClick={() => zoomBy(1 / 1.3)}
          className={cn(ZOOM_BTN, 'border-border border-b')}
        >
          −
        </button>
        <button
          type="button"
          aria-label="Fit graph to view"
          onClick={fitToView}
          className={cn(ZOOM_BTN, 'text-[11px]')}
        >
          Fit
        </button>
      </div>
    </div>
  )
}
